import classNames from 'classnames'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'

import { Body2, Caption1 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { PopoverInput } from '@ui/components/Popover'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { CaretIcon } from '@ui/icons/Caret.icon'

import { PageMetaData } from '@utils/ApiClient'
import { useWindowResize } from '@utils/domUtils'
import { isOne, isZero } from '@utils/validations'

import { Box } from '@shared/components/Box'

import styles from './Pagination.module.scss'

const defaultPageSizes = [10, 20, 50, 100]
const pageSizesDivisibleByThree = [12, 24, 48, 102]

interface Props {
  pageData?: PageMetaData
  isLoading?: boolean
  maxPageSize?: number
  minPageSize?: number
  isPageSizeDivisibleByThree?: boolean
  pageSizeOptions?: number[]
  onPageChanged(newPage: number, pageSize?: number, eventType?: PaginationEventType): void
  scrollToHeaderId?: string
  hidePageSize?: boolean
}

export enum PaginationEventType {
  PAGE_CHANGED = 'pageChanged',
  ENTRIES_CHANGED = 'entriesChanged',
}

export const Pagination: React.FC<Props> = (props) => {
  const {
    pageData,
    onPageChanged,
    maxPageSize,
    isPageSizeDivisibleByThree = false,
    pageSizeOptions: pageSizeOptionsProp,
  } = props
  const isMobile = useWindowResize()
  const pageSizeOptions =
    pageSizeOptionsProp ??
    (isPageSizeDivisibleByThree ? pageSizesDivisibleByThree : defaultPageSizes)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleMaxEntriesExceedTotalResults = (totalResults: number) => {
    const maxResultsPerPageIndex = pageSizeOptions.findIndex((pageSize) => pageSize > totalResults)

    if (maxResultsPerPageIndex !== -1) {
      return pageSizeOptions
        .slice(0, maxResultsPerPageIndex + 1)
        .slice()
        .reverse()
    }

    return pageSizeOptions.slice(0, 4).slice().reverse()
  }

  const pageSizes = useMemo(() => {
    const totalResults = props.pageData?.totalResults || 0

    if (!totalResults) {
      return []
    }

    if (maxPageSize && maxPageSize > totalResults) {
      return handleMaxEntriesExceedTotalResults(totalResults)
    }

    return pageSizeOptions
      .filter((size, index) => {
        const previousSize = index === 0 ? 0 : pageSizeOptions[index - 1]
        const threshold = previousSize + 1

        const withinMaxPageSize = !maxPageSize || size <= maxPageSize
        const withinTotalResults = (maxPageSize || totalResults) >= threshold

        return withinMaxPageSize && withinTotalResults
      })
      .slice()
      .reverse()
  }, [
    props.pageData?.totalResults,
    maxPageSize,
    pageSizeOptions,
    handleMaxEntriesExceedTotalResults,
  ])

  if (!pageData) {
    return null
  }

  if (props.pageData?.totalResults && props.pageData?.totalResults < pageSizeOptions[0] + 1) {
    return null
  }

  return (
    <Box
      className={styles.paginationSpace}
      flex
      align='center'
      justify='space-between'
      direction={isMobile ? 'column' : 'row'}
      mt='x16'
      mb='x24'
      gap='x24'
      position='relative'
    >
      {pageData.totalPages > 1 ? <PaginationPage {...props} /> : <div />}
      {pageSizes.length && !props.hidePageSize ? (
        <PaginationSize {...props} pageSizes={pageSizes} onChange={onPageChanged} />
      ) : (
        <div />
      )}
    </Box>
  )
}

const PaginationPage: React.FC<Props> = (props) => {
  const { pageData, onPageChanged } = props
  const { pageIndex } = pageData ?? { pageIndex: 1 }
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const VISIBLE_PAGES = 5
  const debounced = useDebouncedCallback((index, pageSize) => onPageChanged(index, pageSize), 500)

  const handlePageChange = async (pageIndex?: number) => {
    if (pageIndex && pageIndex !== pageData?.pageIndex) {
      if (pageIndex < 1) {
        debounced(1, pageData?.pageSize)
      } else if (pageIndex > (pageData?.totalPages ?? 1)) {
        debounced(pageData?.totalPages, pageData?.pageSize)
      } else {
        debounced(pageIndex, pageData?.pageSize)
      }
    }
  }

  if (!pageData) {
    return null
  }

  const isActive = (page: number) => page === pageIndex

  const getPrePages = () => {
    if (pageIndex > VISIBLE_PAGES) {
      return [1]
    }

    return Array.from({ length: Math.min(VISIBLE_PAGES, pageData.totalPages) }, (_, i) => i + 1)
  }

  const getMidPage = () => {
    if (pageIndex > VISIBLE_PAGES && pageIndex < totalPages - (VISIBLE_PAGES - 1)) {
      return [pageIndex]
    }

    return []
  }

  const getPostPages = () => {
    if (totalPages <= VISIBLE_PAGES) {
      return []
    }

    if (pageIndex > totalPages - VISIBLE_PAGES && pageIndex > VISIBLE_PAGES) {
      return Array.from({ length: VISIBLE_PAGES }, (_, i) => totalPages - (VISIBLE_PAGES - 1) + i)
    }

    return [totalPages]
  }

  const { totalPages } = pageData

  const Page = ({ page }: { page: number }) => (
    <Caption1
      color='text-med-em'
      key={page}
      className={classNames(styles.page, { [styles.active]: isActive(page) })}
      onClick={() => handlePageChange(page)}
    >
      {page}
    </Caption1>
  )

  return (
    <Box flex align='center' fullWidth={isMobile}>
      <Box
        flex
        align='center'
        gap='x6'
        fullWidth={isMobile}
        justify={isMobile ? 'space-between' : undefined}
      >
        <Tooltip disabled={isMobile} content={t('Pagination.Previous page')}>
          <IconButton.Ghost
            icon={CaretIcon.Left}
            disabled={isOne(pageIndex)}
            onClick={() => handlePageChange(pageIndex - 1)}
            size='sm'
          />
        </Tooltip>

        <Box flex align='center' gap='x6'>
          {getPrePages().map((page) => (
            <Page key={page} page={page} />
          ))}

          {!!getMidPage().length && <Caption1 color='text-med-em'>...</Caption1>}

          {getMidPage().map((page) => (
            <Page key={page} page={page} />
          ))}

          {!!getPostPages().length && <Caption1 color='text-med-em'>...</Caption1>}

          {getPostPages().map((page) => (
            <Page key={page} page={page} />
          ))}
        </Box>

        <Tooltip disabled={isMobile} content={t('Pagination.Next page')}>
          <IconButton.Ghost
            icon={CaretIcon.Right}
            onClick={() => handlePageChange(pageIndex + 1)}
            disabled={pageIndex === totalPages || isZero(totalPages)}
            size='sm'
          />
        </Tooltip>
      </Box>
    </Box>
  )
}

interface PaginationSizeProps extends Props {
  pageSizes: number[]
  scrollToHeaderId?: string | undefined
  onChange(newPage: number, pageSize?: number, eventType?: PaginationEventType): void
}

const PaginationSize: React.FC<PaginationSizeProps> = (props) => {
  const { pageData, pageSizes, onChange } = props

  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const handleChange = (newPageSize: number) => {
    if (newPageSize !== pageData?.pageSize) {
      if (newPageSize < (pageData?.pageSize ?? 10)) {
        onChange(1, newPageSize, PaginationEventType.ENTRIES_CHANGED)
      } else {
        onChange(1, newPageSize)
      }
    }
  }

  return (
    <Box flex direction='row' align='center' gap='x8'>
      <Body2 color='text-low-em'>{isMobile ? t('Results per page') : t('Show results')}</Body2>
      <PopoverInput
        value={pageData?.pageSize}
        options={pageSizes.map((size) => ({
          id: size,
          name: String(size),
        }))}
        onChange={(option) => handleChange(option.id)}
        size='lg'
        closeOnOutOfSight
      />
    </Box>
  )
}
